import { EmberTopBarArea } from '@/src/components/Ember/EmberTopBarArea'
import { useCompanySelectStore } from '@/src/components/Navbar/CompanySelect/companySelectStore'
import { NavbarAuthenticated, NavbarAuthenticatedProfile } from '@/src/components/Navbar/NavbarAuthenticated'
import { UserMenu } from '@/src/components/Navbar/NavbarAuthenticated/UserMenu'
import { NewVersionBanner } from '@/src/components/NewVersionBanner'
import { NotificationInbox } from '@/src/components/NotificationInbox'
import { FullScreenLoader } from '@/src/components/loading/FullScreanLoader'
import { InsightsProvider } from '@/src/contexts/InsightsContext'
import { LocationsProvider } from '@/src/contexts/LocationsContext'
import { SeriesProvider } from '@/src/contexts/SeriesContext'
import { BackButton } from '@/src/features/analytics/components/BackButton'
import { WidgetsSideBar } from '@/src/features/dashboard/presentation/components/WidgetsSideBar'
import {
  MeasureDetailHeaderSlotProvider,
  useMeasureDetailHeaderSlot,
} from '@/src/features/iso-suite/measures/mesaure-detail/MeasureDetailHeaderSlotContext'
import { useLocationModules } from '@/src/hooks/location-module/location-module.hooks'
import { useSessionStore } from '@/src/hooks/use_session_store'
import ErrorBoundary from '@/src/shared/ErrorBoundary'
import { appColors } from '@/src/theme'
import { LoadingPercent } from '@/src/widgets/LoadingPercent/LoadingPercent'
import { useProcurementInfo } from '@/src/widgets/SpotMarketPrice/hooks/useProcurementInfo'
import { Box, Divider, Grid } from '@chakra-ui/react'
import { FlexRow, LocationMainSelect } from '@components'
import { useEmberStore } from '@features/Ember/infrastructure/useEmberStore'
import { useDashboardEditingStore } from '@features/dashboard/infrastructure/useDashboardEditingStore'
import {
  ANALYSES_BASE_ROUTE,
  CENTER_CONTENT_PADDING_BOTTOM,
  EFFICIENCY_AND_MEASURES_BASE_ROUTE,
  ENERGY_COST_BASE_ROUTE,
  HEADER_BAR_HEIGHT,
  MEASURES_BASE_ROUTE,
  MESSSTELLEN_BASE_ROUTE,
  PROCUREMENT_AND_MARKETS_BASE_ROUTE,
  REPORTS_BASE_ROUTE,
  TABS,
} from '@utils/constants'
import { useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import useFooterStore from '../useFooterStore'
import { EmberSidebar } from './sidebars/EmberSidebar'

const LeftAreaContent = () => {
  const location = useLocation()
  const isProfilePage = location.pathname.startsWith('/profile')
  const { isInEditMode } = useDashboardEditingStore()
  if (isInEditMode) return null

  return (
    <FlexRow height='full'>
      {<NavbarAuthenticated />}
      {isProfilePage && (
        <Box borderRightWidth={1} borderRightColor='gray.300'>
          <NavbarAuthenticatedProfile />
        </Box>
      )}
    </FlexRow>
  )
}

const RightAreaContent = () => {
  const { isInEditMode } = useDashboardEditingStore()
  const { isSidebarShowing: isEmberOpen } = useEmberStore()

  if (isInEditMode)
    return (
      <InsightsProvider>
        <WidgetsSideBar />
      </InsightsProvider>
    )

  if (isEmberOpen)
    return (
      <InsightsProvider>
        <EmberSidebar />
      </InsightsProvider>
    )

  return null
}

const TopAreaContent = () => {
  const measureDetailHeader = useMeasureDetailHeaderSlot()
  // TODO: V Remove special cases for analytics. Back button of analytics have specific functionality that we could apply also here. (The delete modal when something changed)
  const analyticsDetailPaths = [
    '/analytics/baseline/',
    '/analytics/regression/',
    '/analytics/visualization/',
    '/analytics/batch/',
    '/analytics/data-management/',
    '/analytics/comparison/',

    // new base product analyses navigation
    `${ANALYSES_BASE_ROUTE}/baseline/`,
    `${ANALYSES_BASE_ROUTE}/regression/`,
    `${ANALYSES_BASE_ROUTE}/visualization/`,
    `${ANALYSES_BASE_ROUTE}/batch/`,
    `${ANALYSES_BASE_ROUTE}/data-management/`,
    `${ANALYSES_BASE_ROUTE}/comparison/`,
    `${MEASURES_BASE_ROUTE}/${TABS.measureHub.analyticsBaseline}/`,
    // Documentation: /efficiency-and-measures/documentation/:documentId OR /documentation/new/(ecoplanet|document|link)
    new RegExp(`^${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/documentation/(?:new/(?:ecoplanet|document|link)|[^/]+)(?:/)?$`),
  ]

  const detailPathPatterns = [
    // Messstellen detail pages only: /messstellen/:deviceId (exclude overview, custom-views, energy-evaluation,
    // awareness, and enpi - the EC-5827 EnPI overview tab, whose own detail/new/edit pages are matched below;
    // allow optional trailing slash)
    /^\/messstellen\/(?!overview(?:\/)?$|custom-views(?:\/)?$|energy-evaluation(?:\/)?$|awareness(?:\/)?$|enpi(?:\/)?$)[^\/]+(?:\/)?$/,

    // Procurement (legacy)
    '/procurement/overview/',
    '/procurement/energy-markets/',
    '/procurement/invoice-audits/',
    '/energy-manager-pro/documentation/',
    '/energy-manager-pro/measures/',
    '/energy-manager-pro/enpi/',
    '/energy-manager-pro/checklist',
    '/energy-manager-pro/valeri/',

    // Efficiency & Measures: show back ONLY on detail pages, not on root listings
    // Measures detail: /efficiency-and-measures/measures/:measureId
    new RegExp(`^${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/measures/[^/]+(?:/)?$`),
    // Checklist task only (not checklist root): /efficiency-and-measures/checklist/task
    new RegExp(`^${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/checklist/task(?:/)?$`),
    // Valeri detail/create/edit: /efficiency-and-measures/valeri/(create|:valeriId|:valeriId/edit)
    new RegExp(`^${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/valeri/(?:create|[^/]+(?:/edit)?)(?:/)?$`),

    new RegExp(`^${MEASURES_BASE_ROUTE}/measures/[^/]+(?:/)?$`),
    new RegExp(`^${MEASURES_BASE_ROUTE}/valeri/(?:create|[^/]+(?:/edit)?)(?:/)?$`),

    new RegExp(`^${REPORTS_BASE_ROUTE}/reports/[^/]+(?:/sent-reports/[^/]+)?(?:/)?$`),

    // ENPI detail/new/edit: /measures/enpi/(new|:enpiId|:enpiId/edit)
    new RegExp(`^${MEASURES_BASE_ROUTE}/enpi/(?:new|[^/]+(?:/edit)?)(?:/)?$`),
    // EC-5827: ENPI detail/new/edit under Messstellen: /messstellen/enpi/(new|:enpiId|:enpiId/edit)
    new RegExp(`^${MESSSTELLEN_BASE_ROUTE}/${TABS.messstellen.enpi}/(?:new|[^/]+(?:/edit)?)(?:/)?$`),

    new RegExp(`^${MEASURES_BASE_ROUTE}/simulation/[^/]+(?:/)?$`),
    // Energy cost and procurement & markets sections
    `${ENERGY_COST_BASE_ROUTE}/${TABS.energiekosten.audit}/`,
    `${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.procurement}/`,
    `${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.markets}/`,
  ]

  //TODO: include navigation back for new routes

  const location = useLocation()
  const { user } = useSessionStore()
  const { isInEditMode } = useDashboardEditingStore()
  const navigate = useNavigate()

  const customNavigationBackConfiguration = [
    { pathMatcher: `${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/${TABS.efficiencyAndMeasures.documentation}/`, navigate: -1 },
  ]

  const isDetailPage = patterns =>
    patterns.some(pattern => {
      if (pattern instanceof RegExp) {
        return pattern.test(location.pathname)
      }
      return location.pathname.startsWith(pattern)
    })

  const getPreviousUrl = () => {
    const trimmedPath = location.pathname.replace(/\/[^/]+\/?$/, '')
    return trimmedPath
  }

  if (location.pathname.startsWith('/profile') || isInEditMode || isDetailPage(analyticsDetailPaths)) {
    return null
  }
  const hasNavigationHistory = () => {
    return window.history.length > 1
  }

  const renderHeaderContent = () => {
    if (isDetailPage(detailPathPatterns)) {
      const customNavigationHandling = customNavigationBackConfiguration.find(config => location.pathname.includes(config.pathMatcher))

      return (
        <FlexRow
          alignContent={'center'}
          alignItems={'center'}
          justifyContent='space-between'
          width='100%'
          mb='4px'
          height={'48px'}
          pr='8px'
        >
          <BackButton
            onClick={() => {
              if (customNavigationHandling && typeof customNavigationHandling.navigate === 'number') {
                navigate(customNavigationHandling.navigate)
              } else if (hasNavigationHistory()) {
                // Navigate back in browser history
                navigate(-1)
              } else {
                // Fallback: construct parent URL if no history exists
                const previousUrl = getPreviousUrl()
                navigate(previousUrl)
              }
            }}
          />
          {measureDetailHeader?.headerRight ? (
            <FlexRow alignItems='center' flexShrink={0}>
              {measureDetailHeader.headerRight}
            </FlexRow>
          ) : null}
        </FlexRow>
      )
    }
    return (
      <FlexRow alignContent={'center'} alignItems={'center'} mb='4px'>
        <LocationMainSelect />
        {user?.appReloadRequired && <NewVersionBanner />}

        <FlexRow ml='auto' mr='0px' alignItems={'center'}>
          <EmberTopBarArea />
          <NotificationInbox />
          <Divider orientation='vertical' height='30px' ml='8px' mr='8px' />
          <UserMenu />
        </FlexRow>
      </FlexRow>
    )
  }

  return (
    <Box height={HEADER_BAR_HEIGHT} pl='24px' pr='10px' pt='4px' mb={'HEADER_BAR_MARGIN_BOTTOM'} bg={appColors.background}>
      {renderHeaderContent()}
      <Divider />
    </Box>
  )
}

const CenterAreaContent = () => {
  const parentRef = useRef<HTMLDivElement>(null)

  // Fetch energy prices and cache it with react query as it takes a while for customers with many locations.
  // TODO: Remove this once this is out: https://linear.app/ecoplanet/issue/EC-2614/daily-consumption-table
  useProcurementInfo()

  const { isSwitchingCompany } = useCompanySelectStore()

  // remove general margin for ember screen
  const location = useLocation()
  const dashboardScreen = location.pathname.startsWith('/dashboard') // dashboard will manage its own padding due to layout grid component requires its own spacing
  const padding = dashboardScreen ? '0px' : `0px 10px ${CENTER_CONTENT_PADDING_BOTTOM} 24px`
  return (
    <Box p={padding} ref={parentRef}>
      {isSwitchingCompany ? <LoadingPercent isVisible={isSwitchingCompany} /> : <Outlet />}
    </Box>
  )
}

export const LayoutMain = () => {
  const { user } = useSessionStore()
  const { isFetched } = useLocationModules()
  const isEmberSidebarShowing = useEmberStore().isSidebarShowing
  const isDashboardInEditMode = useDashboardEditingStore().isInEditMode
  const shouldHideScrollbar = isEmberSidebarShowing || isDashboardInEditMode
  const mainContentClassName = `${shouldHideScrollbar ? 'hide-scrollbar' : ''} innerScrollBar`
  const topRightItem = isEmberSidebarShowing ? 'top-area' : 'right-area'
  const { footerContent } = useFooterStore()

  if (!isFetched) {
    return <FullScreenLoader />
  }

  return (
    <LocationsProvider key={`${user?.companyId}:${user?.id}`}>
      <SeriesProvider>
        <MeasureDetailHeaderSlotProvider>
          <Grid
            id='layout'
            // for responsive layouts you can pass an object here with breakpoints (e.g. {md: "left-area ..."", xs:"top-area ..."}  )
            templateAreas={`
            "left-area top-area ${topRightItem}"
            "left-area center-area right-area"
            ${footerContent ? '"left-area footer-area right-area"' : ''}
          `}
            gridTemplateColumns='auto 1fr auto'
            gridTemplateRows='auto 1fr auto'
            height='100vh'
            bgColor={appColors.background}
          >
            <ErrorBoundary boundaryType='content'>
              <Box as='header' gridArea='top-area'>
                <TopAreaContent />
              </Box>
            </ErrorBoundary>

            <ErrorBoundary boundaryType='content'>
              <Box as='aside' gridArea='left-area'>
                <LeftAreaContent />
              </Box>
            </ErrorBoundary>

            <ErrorBoundary boundaryType='content'>
              <Box as='main' gridArea='center-area' overflow='auto' className={mainContentClassName} bgColor={appColors.background}>
                <CenterAreaContent />
              </Box>
            </ErrorBoundary>

            <ErrorBoundary boundaryType='content'>
              <Box as='aside' gridArea='right-area'>
                <RightAreaContent />
              </Box>
            </ErrorBoundary>

            {footerContent && (
              <ErrorBoundary boundaryType='content'>
                <Box as='footer' gridArea='footer-area'>
                  {footerContent}
                </Box>
              </ErrorBoundary>
            )}
          </Grid>
        </MeasureDetailHeaderSlotProvider>
      </SeriesProvider>
    </LocationsProvider>
  )
}
